import { Component, OnInit, inject, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../shared/shared.module';
import { MentorPanelService } from '../../core/services/mentor-panel/mentor-panel.service';
import { MentorSessionHistoryItem, MentorSessionHistoryRequest, MentorHistoryRecord, SessionStatus } from '../../core/models/mentor-panel/mentor-panel-api-models';
import { StatusBadgeComponent } from '../../shared/status-badge/status-badge.component';


@Component({
    selector: 'app-session-history',
    standalone: true,
    imports: [CommonModule, SharedModule, StatusBadgeComponent],
    templateUrl: './session-history.component.html',
    styleUrl: './session-history.component.scss'
})
export class SessionHistoryComponent implements OnInit {
    private mentorPanelService = inject(MentorPanelService);
    readonly SessionStatus = SessionStatus;

    @ViewChild('pageSizeDropdownRef') pageSizeDropdownRef!: ElementRef;

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.pageSizeDropdownRef && !this.pageSizeDropdownRef.nativeElement.contains(event.target)) {
            this.pageSizeDropdownOpen = false;
        }
    }
    activeFilter: SessionStatus = SessionStatus.ALL;
    filters = [
        { label: 'All', value: SessionStatus.ALL },
        { label: 'Completed', value: SessionStatus.COMPLETED },
        { label: 'Rejected', value: SessionStatus.REJECTED },
        { label: 'Cancelled', value: SessionStatus.CANCELLED }
    ];

    history: MentorHistoryRecord[] = [];
    isLoading: boolean = true;
    totalCount: number = 0;
    currentPage: number = 1;
    pageSize: number = 10;
    pageSizeOptions: number[] = [5, 10, 20, 50];
    pageSizeDropdownOpen: boolean = false;

    get totalPages(): number {
        return Math.ceil(this.totalCount / this.pageSize) || 1;
    }

    getEndItem(): number {
        return Math.min(this.currentPage * this.pageSize, this.totalCount);
    }

    constructor() { }

    ngOnInit(): void {
        this.fetchHistory();
    }

    fetchHistory(): void {
        const request: MentorSessionHistoryRequest = {
            filter: this.activeFilter,
            page: this.currentPage,
            limit: this.pageSize
        };

        this.isLoading = true;
        this.mentorPanelService.getSessionHistory(request).subscribe({
            next: (response) => {
                if (response.Success) {
                    this.history = response.Data.sessions.map((s, idx) => this.mapToHistoryRecord(s, idx));
                    this.totalCount = response.Data.total_count;
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error fetching session history', err);
                this.isLoading = false;
            }
        });
    }

    private mapToHistoryRecord(item: MentorSessionHistoryItem, idx: number): MentorHistoryRecord {
        return {
            id: idx.toString(), // History record items don't have id in MentorSessionHistoryItem, using index
            studentName: item.student_name,
            initials: item.student_initials,
            date: item.date,
            duration: item.duration,
            topic: item.topic,
            status: item.status,
            rating: item.rating ? item.rating.toString() : null,
            avatarColorClass: this.getRandomAvatarColor(item.student_name)
        };
    }

    private getRandomAvatarColor(name: string): string {
        const colors = ['blue-avatar', 'purple-avatar', 'orange-avatar', 'green-avatar'];
        const index = name.length % colors.length;
        return colors[index];
    }



    setFilter(filter: SessionStatus) {
        this.activeFilter = filter;
        this.currentPage = 1;
        this.fetchHistory();
    }

    prevPage(): void {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.fetchHistory();
        }
    }

    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.fetchHistory();
        }
    }

    onPageSizeChange(): void {
        this.currentPage = 1;
        this.fetchHistory();
    }

    selectPageSize(size: number): void {
        this.pageSize = size;
        this.pageSizeDropdownOpen = false;
        this.currentPage = 1;
        this.fetchHistory();
    }
}
